import DataGrid, {
    KeyboardNavigation,
    Pager,
    Paging,
    SearchPanel,
    Export,
    Column,
    MasterDetail
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { pageFormats, allowedPageSizes } from '../../components/GlobalDataGridConfigurations';
import CustomPreparedHeaderCell from '../../components/CustomPreparedHeaderCell';
import InfoIconComponent from '../../components/CustomInfoIcon';
import { FOUOheader, FOUOfooter } from '../../components/FOUO';
import { useAuth } from '../../hooks/useAuth';
import ExportGrid from '../../components/ExportGrid';
import dayjs from 'dayjs';

const OccupancyAgreementChanges = ({ data }) => {
    const { user } = useAuth();

    const dataSource = new CustomStore({
        key: 'AuditTrailNumber',
        load: async () => {
            // console.log("OA data", data);
            return data.map(element => ({
                AuditTrailNumber: element.AuditTrailNumber ?? "N/A",
                ChangeType: element.ChangeType ?? "N/A",
                DateChanged: element.DateChanged ? dayjs(element.DateChanged).utc().format("MM/DD/YYYY h:mma") : "N/A",
                UpdatedBy: element.user ?? "N/A",
                OccupancyAgreementId: element.InitialValue?.OccupancyAgreementId,
                initial: {
                    OANumber: element.InitialValue?.OANumber ?? "N/A",
                    LeaseNumber: element.InitialValue?.LeaseNumber ?? "N/A",
                    TotalRentSqFt: element.InitialValue?.TotalRentSqFt ?? "N/A",
                    JURentSqFt: element.InitialValue?.JURentSqFt ?? "N/A",
                    RUFactor: element.InitialValue?.RUFactor ?? "N/A",
                    MemberName: element.InitialValue?.MemberName ?? "N/A",
                    Inactive: element.InitialValue?.Inactive ?? "N/A",
                    CustomerAccount: element.InitialValue?.BuildingByCustomer?.CustomerAccount?.AgencyName ?? "N/A",
                    Building: element.InitialValue?.BuildingByCustomer?.Building?.BuildingSequenceNumber ?? "N/A",
                },
                changed: {
                    OANumber: element.ChangedValue?.OANumber ?? "N/A",
                    LeaseNumber: element.ChangedValue?.LeaseNumber ?? "N/A",
                    TotalRentSqFt: element.ChangedValue?.TotalRentSqFt ?? "N/A",
                    JURentSqFt: element.ChangedValue?.JURentSqFt ?? "N/A",
                    RUFactor: element.ChangedValue?.RUFactor ?? "N/A",
                    MemberName: element.ChangedValue?.MemberName ?? "N/A",
                    Inactive: element.ChangedValue?.Inactive ?? "N/A",
                    CustomerAccount: element.ChangedValue?.BuildingByCustomer?.CustomerAccount?.AgencyName ?? "N/A",
                    Building: element.ChangedValue?.BuildingByCustomer?.Building?.BuildingSequenceNumber ?? "N/A",
                },
            }));
        }
    });

    const pdfFont = () => 7;
    const wrapPdfCell = () => true;

    return (
        <div>
            <FOUOheader
                dateString={dayjs.utc().format('MM/DD/YYYY h:mma') + " UTC"}
                heading={"Audit - Occupancy Agreement Changes"}
                userEmail={user.email}
            />
            <DataGrid
                columnAutoWidth={true}
                dataSource={dataSource}
                onCellPrepared={CustomPreparedHeaderCell}
                showBorders={true}
                showColumnLines={true}
                showRowLines={true}
                rowAlternationEnabled={true}
                onExporting={e => ExportGrid(e, "Audit_Report", wrapPdfCell(), pdfFont())}
            >
                <Column dataField="AuditTrailNumber" caption="Audit Trail Number" visible={false} />
                <Column dataField="DateChanged" caption="Date Changed (UTC)" />
                <Column dataField="ChangeType" caption="Change Type" />
                <Column dataField="UpdatedBy" caption="Changed By" />
                <Column dataField="changed.OANumber" caption="OA Number" />
                <Column dataField="changed.LeaseNumber" caption="Lease Number" />
                <Column dataField="changed.TotalRentSqFt" caption="Total Rent Sq. Ft." />
                <Column dataField="changed.JURentSqFt" caption="Joint Use Rent Sq.Ft." />
                <Column dataField="changed.RUFactor" caption="RU Factor" />
                <Column dataField="changed.MemberName" caption="Member Name" />
                <Column dataField="changed.Inactive" caption="Inactive" />
                <Column dataField="changed.CustomerAccount" caption="Customer Account" />
                <Column dataField="changed.Building" caption="Building Number" />
                <KeyboardNavigation enabled={true} />
                <SearchPanel visible={true} highlightCaseSensitive={true} />
                <Paging defaultPageSize={10} />
                <Pager showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} />
                <Export enabled={true} formats={pageFormats} />
                <MasterDetail
                    enabled={true}
                    render={({ data }) => RenderTemplate(data)}
                />
            </DataGrid>
            <FOUOfooter />
        </div>
    );
};

const changedComponent = (value) => (
    <>
        {value}
        <InfoIconComponent item={value} key={value} />
    </>
);

const RenderTemplate = (values) => {
    if (values.ChangeType === 'Insert' || !values.initial) {
        return <>No Previous History, record was inserted.</>;
    }

    const renderChangedCell = (field) => (cellInfo) => {
        const prev = cellInfo.data.initial?.[field];
        const curr = cellInfo.data.changed?.[field];
        
        // If prev is a boolean, convert to string
        if (typeof prev === 'boolean') {
            return prev !== curr ? changedComponent(`${prev}`) : `${prev}`;
        }
        return prev !== curr ? changedComponent(prev) : prev;
    };

    return (
        <DataGrid
            dataSource={[values]}
            showBorders={true}
            columnAutoWidth={true}
        >
            <Column dataField="initial.OANumber" caption="OA Number" cellRender={renderChangedCell("OANumber")} />
            <Column dataField="initial.LeaseNumber" caption="Lease Number" cellRender={renderChangedCell("LeaseNumber")} />
            <Column dataField="initial.TotalRentSqFt" caption="Total Rent Sq. Ft." cellRender={renderChangedCell("TotalRentSqFt")} />
            <Column dataField="initial.JURentSqFt" caption="Joint Use Rent Sq. Ft." cellRender={renderChangedCell("JURentSqFt")} />
            <Column dataField="initial.MemberName" caption="Member Name" cellRender={renderChangedCell("MemberName")} />
            <Column dataField="initial.Inactive" caption="Inactive" cellRender={renderChangedCell("Inactive")} />
            <Column dataField="initial.CustomerAccount" caption="Customer Account" cellRender={renderChangedCell("CustomerAccount")} />
            <Column dataField="initial.Building" caption="Building Number" cellRender={renderChangedCell("Building")} />
        </DataGrid>
    );
};

export default OccupancyAgreementChanges;
